import React, { useState, useRef, useEffect } from 'react';
import { PlayIcon, PauseIcon } from '@heroicons/react/24/solid';

interface AudioPlayerProps {
  audioUrl: string;
  duration?: number;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, duration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  // Usar la duración recibida como prop, o 0 si no está disponible
  const [audioDuration, setAudioDuration] = useState(
    typeof duration === 'number' && isFinite(duration) && duration > 0 ? duration : 0
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Inicializar el reproductor de audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Manejar eventos del reproductor
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      // Solo actualizar la duración si es un valor válido y no tenemos ya una duración
      if (isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        setAudioDuration(audio.duration);
      }
    };

    const handleDurationChange = () => {
      // También escuchar cambios de duración
      if (isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        setAudioDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleCanPlayThrough = () => {
      // Intentar obtener duración cuando el audio está listo para reproducir
      if (isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        setAudioDuration(audio.duration);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Efecto para manejar la duración proporcionada como prop
  useEffect(() => {
    // Solo actualizar si el valor de duración es válido
    if (typeof duration === 'number' && isFinite(duration) && !isNaN(duration) && duration > 0) {
      setAudioDuration(duration);
    }
  }, [duration]);

  // Reproducir o pausar el audio
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(error => {
        console.error('Error al reproducir audio:', error);
      });
    }

    setIsPlaying(!isPlaying);
  };

  // Actualizar la posición de reproducción al hacer clic en la barra de progreso
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;

    if (!audio || !progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * audioDuration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Formatear tiempo (segundos a MM:SS)
  const formatTime = (timeInSeconds: number) => {
    // Asegurarse de que el tiempo sea un número válido
    if (isNaN(timeInSeconds) || !isFinite(timeInSeconds) || timeInSeconds < 0) {
      return '00:00';
    }
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center space-x-3 w-full max-w-xs bg-gray-100 dark:bg-gray-800 p-2 rounded-lg">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <button
        onClick={togglePlayPause}
        className="flex-shrink-0 bg-domired-600 text-white p-2 rounded-full hover:bg-domired-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
        aria-label={isPlaying ? "Pausar audio" : "Reproducir audio"}
      >
        {isPlaying ? (
          <PauseIcon className="h-5 w-5" />
        ) : (
          <PlayIcon className="h-5 w-5" />
        )}
      </button>

      <div className="flex-grow flex flex-col">
        <div className="flex items-center mb-1">
          <div className="w-2 h-2 rounded-full bg-domired-500 mr-1"></div>
          <span className="text-xs font-medium text-domired-600 dark:text-domired-400">
            Mensaje de voz
          </span>
        </div>

        <div
          ref={progressRef}
          className="h-2 bg-gray-300 dark:bg-gray-700 rounded-full cursor-pointer"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-domired-600 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${(currentTime / Math.max(audioDuration, 1)) * 100}%` }}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-1 font-mono">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(audioDuration)}</span>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
